import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, dateFnsLocalizer, type View } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { CalendarRange } from 'lucide-react';
import Layout from '../../../../components/Layout';
import { vehicleService, VehicleDTO } from '../../../../services/vehicleService';
import { rentalBookingService, RentalBookingDTO } from '../../../../services/rentalBookingService';
import { customerService, CustomerDTO } from '../../../../services/customerService';
import '../CarRentalPage.css';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({
  format, parse, startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }), getDay, locales,
});

const STATUS_COLORS: Record<string, string> = {
  Draft: '#94a3b8', PendingPayment: '#f59e0b', Confirmed: '#3b82f6',
  Active: '#10b981', Completed: '#6b7280', Cancelled: '#ef4444', NoShow: '#ef4444',
};

interface BookingEvent {
  id: number;
  title: string;
  start: Date;
  end: Date;
  status: string;
}

const toLocalInput = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const SchedulePage: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<BookingEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>('month');
  const [date, setDate] = useState(new Date());

  const [showModal, setShowModal] = useState(false);
  const [customers, setCustomers] = useState<CustomerDTO[]>([]);
  const [vehicles, setVehicles] = useState<VehicleDTO[]>([]);
  const [form, setForm] = useState({ customerId: '', vehicleId: '', pickupDate: '', returnDate: '', pickupLocation: '', returnLocation: '' });
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadBookings = useCallback(() => {
    setLoading(true);
    rentalBookingService.getAll()
      .then((res: any) => {
        const rows: RentalBookingDTO[] = res.data?.data?.data ?? res.data?.data ?? [];
        setEvents(rows.map((b) => ({
          id: b.id,
          title: `${b.vehicleLabel ?? 'Vehicle'} — ${b.customerName ?? 'Customer'}`,
          start: new Date(b.pickupDate),
          end: new Date(b.returnDate),
          status: b.status,
        })));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(loadBookings, [loadBookings]);

  const eventStyleGetter = useCallback((event: BookingEvent) => ({
    style: { backgroundColor: STATUS_COLORS[event.status] || '#3b82f6', borderRadius: '6px', border: 'none', color: '#fff', fontSize: '0.78rem' },
  }), []);

  const openCreateModal = useCallback((pickup: Date, ret: Date) => {
    setForm({ customerId: '', vehicleId: '', pickupDate: toLocalInput(pickup), returnDate: toLocalInput(ret), pickupLocation: '', returnLocation: '' });
    setVehicles([]);
    setError(null);
    setShowModal(true);
    if (customers.length === 0) {
      customerService.getCustomers({ pageNumber: 1, pageSize: 200 }).then((res: any) => setCustomers(res.data?.data?.data ?? res.data?.data ?? []));
    }
  }, [customers.length]);

  const handleSelectSlot = useCallback(({ start, end }: { start: Date; end: Date }) => openCreateModal(start, end), [openCreateModal]);
  const handleSelectEvent = useCallback((event: BookingEvent) => navigate(`/car-rental/bookings`), [navigate]);

  const handleSearchVehicles = async () => {
    if (!form.pickupDate || !form.returnDate) { setError('Pickup and return dates are required.'); return; }
    setSearching(true);
    setError(null);
    try {
      const res: any = await vehicleService.searchAvailable(form.pickupDate, form.returnDate);
      setVehicles(res.data?.data ?? []);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? 'Failed to search vehicles');
    } finally {
      setSearching(false);
    }
  };

  const handleCreate = async () => {
    if (!form.customerId || !form.vehicleId) { setError('Select a customer and a vehicle before booking.'); return; }
    setSaving(true);
    setError(null);
    try {
      await rentalBookingService.create({
        customerId: Number(form.customerId), vehicleId: Number(form.vehicleId),
        pickupDate: form.pickupDate, returnDate: form.returnDate,
        pickupLocation: form.pickupLocation, returnLocation: form.returnLocation,
      });
      setShowModal(false);
      loadBookings();
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? 'Failed to create booking');
    } finally {
      setSaving(false);
    }
  };

  const upcomingCount = useMemo(() => events.filter((e) => e.end >= new Date()).length, [events]);

  return (
    <Layout>
      <div className="ims-page car-rental-page car-rental-schedule">
        <div className="ims-list-header">
          <div>
            <h1 className="ims-list-title">Rental Schedule</h1>
            <p className="ims-list-subtitle">Click a date to create a new rental</p>
          </div>
          <div className="ims-list-actions">
            <button className="btn-refresh" onClick={loadBookings}>Refresh</button>
            <button className="btn-new" onClick={() => openCreateModal(new Date(), new Date(Date.now() + 24 * 60 * 60 * 1000))}>+ New Rental</button>
          </div>
        </div>

        <div className="ims-stats-row">
          <div className="ims-stat-card">
            <div className="ims-stat-icon cr-icon-fleet"><CalendarRange size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{events.length}</span>
              <span className="ims-stat-label">Total Rentals</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon cr-icon-upcoming"><CalendarRange size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{upcomingCount}</span>
              <span className="ims-stat-label">Upcoming / Active</span>
            </div>
          </div>
        </div>

        <div className="ims-card">
          <div className="ims-card-body">
            {loading ? <p>Loading schedule…</p> : (
              <Calendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                style={{ height: 700 }}
                view={view}
                onView={setView}
                date={date}
                onNavigate={setDate}
                selectable
                popup
                eventPropGetter={eventStyleGetter as any}
                onSelectSlot={handleSelectSlot}
                onSelectEvent={handleSelectEvent}
                views={['month', 'week', 'day', 'agenda']}
              />
            )}
          </div>
        </div>

        {showModal && (
          <div className="cr-modal-backdrop" onClick={() => setShowModal(false)}>
            <div className="cr-modal" onClick={(e) => e.stopPropagation()}>
              <div className="ims-card-header">
                <span className="ims-card-title">New Rental</span>
                <button className="ims-back-btn" onClick={() => setShowModal(false)}>✕</button>
              </div>
              <div className="ims-card-body">
                {error && <div className="ims-alert ims-alert-error">{error}</div>}

                <div className="form-group">
                  <label className="form-label">Customer</label>
                  <select className="form-select" value={form.customerId} onChange={(e) => setForm((p) => ({ ...p, customerId: e.target.value }))}>
                    <option value="">Select customer</option>
                    {customers.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.code})</option>)}
                  </select>
                </div>

                <div className="cr-modal-dates">
                  <div className="form-group">
                    <label className="form-label">Pickup Date/Time</label>
                    <input className="form-input" type="datetime-local" value={form.pickupDate} onChange={(e) => setForm((p) => ({ ...p, pickupDate: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Return Date/Time</label>
                    <input className="form-input" type="datetime-local" value={form.returnDate} onChange={(e) => setForm((p) => ({ ...p, returnDate: e.target.value }))} />
                  </div>
                </div>

                <div className="cr-modal-dates">
                  <div className="form-group">
                    <label className="form-label">Pickup Location</label>
                    <input className="form-input" value={form.pickupLocation} onChange={(e) => setForm((p) => ({ ...p, pickupLocation: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Return Location</label>
                    <input className="form-input" value={form.returnLocation} onChange={(e) => setForm((p) => ({ ...p, returnLocation: e.target.value }))} />
                  </div>
                </div>

                <button className="ims-btn ims-btn-secondary" onClick={handleSearchVehicles} disabled={searching}>
                  {searching ? 'Searching…' : 'Search Available Vehicles'}
                </button>

                {vehicles.length > 0 && (
                  <div className="cr-vehicle-grid">
                    {vehicles.map((v) => (
                      <div
                        key={v.id}
                        className="ims-card cr-vehicle-card"
                        style={{ border: form.vehicleId === String(v.id) ? '2px solid var(--ims-accent)' : '1px solid var(--ims-border)' }}
                        onClick={() => setForm((p) => ({ ...p, vehicleId: String(v.id) }))}
                      >
                        <div className="ims-card-body">
                          <strong>{v.make} {v.model}</strong>
                          <p className="cr-vehicle-meta">{v.registrationNumber} · {v.year}</p>
                          <p className="cr-vehicle-rate">${v.rentalRate.toFixed(2)} / day</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="cr-modal-footer">
                <button className="ims-btn ims-btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button className="ims-btn ims-btn-primary" onClick={handleCreate} disabled={saving || !form.vehicleId}>
                  {saving ? 'Booking…' : 'Confirm Rental'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SchedulePage;
